import React from 'react';
import { AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { ScraperQueuedRun } from '../services/api';

/** 'starting': cron took the request but no progress has arrived yet. */
export type RunStartState = 'idle' | 'starting' | 'failed';

// Cron checks for a queued run every two minutes, so waiting longer means it isn't being picked up.
const QUEUE_WARN_SECONDS = 150;

const formatWait = (seconds: number) =>
  seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

const Banner: React.FC<{
  tone: 'brand' | 'amber' | 'rose';
  icon: React.ReactNode;
  title: string;
  detail: string;
}> = ({ tone, icon, title, detail }) => {
  const border = { brand: 'border-brand-200', amber: 'border-amber-200', rose: 'border-rose-200' }[tone];
  return (
    <div
      role="status"
      aria-live="polite"
      className={`bg-surface rounded-xl shadow-sm border ${border} p-5 mb-6 flex items-start gap-3`}
    >
      <span className="flex-shrink-0 mt-0.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink-900">{title}</p>
        <p className="text-xs text-ink-500 mt-0.5 tabular-nums">{detail}</p>
      </div>
    </div>
  );
};

/** What happened to a Run press before the scrape shows up as in progress. */
const RunQueueBanner: React.FC<{
  queuedRun: ScraperQueuedRun | null;
  startState: RunStartState;
}> = ({ queuedRun, startState }) => {
  if (queuedRun) {
    const party = queuedRun.party_size ? `party of ${queuedRun.party_size}` : 'unknown party size';
    const waited = formatWait(queuedRun.waiting_seconds);
    if (queuedRun.waiting_seconds > QUEUE_WARN_SECONDS) {
      return (
        <Banner
          tone="amber"
          icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
          title={`Scrape queued · ${party} · still waiting`}
          detail={`Queued ${waited} ago. The server checks every 2 minutes, so its cron job may not be picking this up.`}
        />
      );
    }
    return (
      <Banner
        tone="brand"
        icon={<Clock className="w-5 h-5 text-brand-500 animate-pulse" />}
        title={`Scrape queued · ${party}`}
        detail={`Queued ${waited} ago. The server checks every 2 minutes and will start it then.`}
      />
    );
  }

  if (startState === 'starting') {
    return (
      <Banner
        tone="brand"
        icon={<Loader2 className="w-5 h-5 text-brand-500 animate-spin" />}
        title="Starting scrape…"
        detail="The server has picked it up. Progress appears here in a few seconds."
      />
    );
  }

  if (startState === 'failed') {
    return (
      <Banner
        tone="rose"
        icon={<AlertTriangle className="w-5 h-5 text-rose-500" />}
        title="The queued scrape didn't start"
        detail="The server picked it up but no progress arrived. Check backend/logs/scraper-manual.log on the server."
      />
    );
  }

  return null;
};

export default RunQueueBanner;
